import { ActionCreator } from 'redux';
import { LogSummary, IntervalType, TrackedLink, LinkContacts, BouncedCategorySummary } from '@dev/ee-api/ee-api-models';
import {
    LoadSummaryFetchStartAction,
    LoadSummaryFetchSuccessAction,
    LoadSummaryFetchErrorAction,
    LoadTodaySummaryFetchStartAction,
    LoadTodaySummaryFetchSuccessAction,
    LoadTodaySummaryFetchErrorAction,
    LoadLinkTrackingListFetchStartAction,
    LoadLinkTrackingListFetchSuccessAction,
    LoadLinkTrackingFetchErrorAction,
    FindLinkTrackingAction,
    ExportLinksAction,
    ExportLinksErrorAction,
    ExportLinksStartAction,
    ExportLinksSuccessAction,
    LoadLinkContactsStartAction,
    LoadLinkContactsSuccessAction,
    LoadLinkContactsErrorAction,
    ExportLinkContactsAction,
    ExportLinkContactsErrorAction,
    ExportLinkContactsStartAction,
    ExportLinkContactsSuccessAction,
    LoadBouncedSummaryAction,
    BouncedSummaryTypes,
    LoadBouncedSummarySuccessAction,
    StatisticsState,
} from './types';
import { LinkTrackingParams, ExportLinksParams, ExportLinkContactsParams, LinkContactsParams } from '@api/services/ElasticApi';
import { store } from '@shared-store/shared-store';
import { ApplicationState } from '..';

export const LoadSummaryStart: ActionCreator<LoadSummaryFetchStartAction> = (
    params: LoadSummaryStartParams
): LoadSummaryFetchStartAction => {
    return {
        type: '@@statistics/LOADSUMMARY_FETCH_START',
        payload: {
            from: params.from,
            to: params.to,
            channelName: params.channelName,
            summaryLoading: true,
        },
    };
};
export const LoadSummarySuccess: ActionCreator<LoadSummaryFetchSuccessAction> = (summary: LogSummary): LoadSummaryFetchSuccessAction => {
    return {
        type: '@@statistics/LOADSUMMARY_FETCH_SUCCESS',
        payload: {
            summary,
            summaryLoading: false,
        },
    };
};
export const LoadSummaryError: ActionCreator<LoadSummaryFetchErrorAction> = (): LoadSummaryFetchErrorAction => {
    return {
        type: '@@statistics/LOADSUMMARY_FETCH_ERROR',
        payload: {
            summaryLoading: false,
        },
    };
};

export const LoadBouncedSummary: ActionCreator<LoadBouncedSummaryAction> = (params: {
    to: string;
    from: string;
    channelName: string;
}): LoadBouncedSummaryAction => ({
    type: BouncedSummaryTypes.LOAD,
    payload: {
        from: params.from,
        to: params.to,
        channelName: params.channelName,
    },
});
export const LoadBouncedSummarySuccess: ActionCreator<LoadBouncedSummarySuccessAction> = (
    bouncedSummary: BouncedCategorySummary
): LoadBouncedSummarySuccessAction => ({
    type: BouncedSummaryTypes.SUCCESS,
    payload: {
        bouncedSummary,
    },
});

export type LoadSummaryStartParams = {
    from: string;
    to: string;
    channelName?: string;
    interval?: IntervalType;
    transactionID?: string;
};

export const LoadTodaySummaryStart: ActionCreator<LoadTodaySummaryFetchStartAction> = (
    params: LoadSummaryStartParams
): LoadTodaySummaryFetchStartAction => {
    return {
        type: '@@statistics/LOADTODAYSUMMARY_FETCH_START',
        payload: {
            from: params.from,
            to: params.to,
            channelName: params.channelName,
            interval: params.interval,
            transactionID: params.transactionID,
            todaySummaryLoading: true,
        },
    };
};
export const LoadTodaySummarySuccess: ActionCreator<LoadTodaySummaryFetchSuccessAction> = (
    todaySummary: LogSummary
): LoadTodaySummaryFetchSuccessAction => {
    return {
        type: '@@statistics/LOADTODAYSUMMARY_FETCH_SUCCESS',
        payload: {
            todaySummary,
            todaySummaryLoading: false,
        },
    };
};
export const LoadTodaySummaryError: ActionCreator<LoadTodaySummaryFetchErrorAction> = (): LoadTodaySummaryFetchErrorAction => {
    return {
        type: '@@statistics/LOADTODAYSUMMARY_FETCH_ERROR',
        payload: {
            todaySummaryLoading: false,
        },
    };
};

export const LoadLinkTrackingListStart: ActionCreator<LoadLinkTrackingListFetchStartAction> = (
    params: LinkTrackingParams
): LoadLinkTrackingListFetchStartAction => {
    return {
        type: '@@statistics/LOADLINKTRACKING_START',
        payload: {
            ...params,
            linktrackingLoading: true,
        },
    };
};
export const LoadLinkTrackingListSuccess: ActionCreator<LoadLinkTrackingListFetchSuccessAction> = (
    linkTrackingList: TrackedLink[],
    totalLinkCount: number,
    linkTrackingActivePage: number,
    linkTrackingPerPage: number
): LoadLinkTrackingListFetchSuccessAction => {
    return {
        type: '@@statistics/LOADLINKTRACKING_SUCCESS',
        payload: {
            linkTrackingList,
            totalLinkCount,
            linkTrackingActivePage,
            linkTrackingPerPage,
            linktrackingLoading: false,
        },
    };
};
export const LoadLinkTrackingListError: ActionCreator<LoadLinkTrackingFetchErrorAction> = (): LoadLinkTrackingFetchErrorAction => {
    return {
        type: '@@statistics/LOADLINKTRACKING_ERROR',
        payload: {
            linktrackingLoading: false,
        },
    };
};

export const FindLinksTracking: ActionCreator<FindLinkTrackingAction> = (params: LinkTrackingParams): FindLinkTrackingAction => {
    const activePage = store.getState().statistics.linkTrackingActivePage;

    if (activePage > 1) {
        params.activePage = 1;
    }

    return {
        type: '@@statistics/LINKTRACKING_SEARCH',
        payload: {
            ...params,
            linktrackingLoading: true,
        },
    };
};

export const ExportLinks: ActionCreator<ExportLinksAction> = (params: ExportLinksParams): ExportLinksAction => ({
    type: '@@statistics/EXPORT_LINKS',
    payload: params,
});
export const ExportLinksStart: ActionCreator<ExportLinksStartAction> = (): ExportLinksStartAction => ({
    type: '@@statistics/EXPORT_LINKS_START',
});
export const ExportLinksError: ActionCreator<ExportLinksErrorAction> = (): ExportLinksErrorAction => ({
    type: '@@statistics/EXPORT_LINKS_ERROR',
});
export const ExportLinksSuccess: ActionCreator<ExportLinksSuccessAction> = (): ExportLinksSuccessAction => ({
    type: '@@statistics/EXPORT_LINKS_SUCCESS',
});

export const LoadLinkContactsStart: ActionCreator<LoadLinkContactsStartAction> = (
    params: LinkContactsParams
): LoadLinkContactsStartAction => {
    return {
        type: '@@statistics/LINKCONTACTS_START',
        payload: {
            ...params,
            linkContactsLoading: true,
        },
    };
};
export const LoadLinkContactsSuccess: ActionCreator<LoadLinkContactsSuccessAction> = (
    linkContactsList: LinkContacts,
    totalLinkContactsCount: number,
    linkContactsActivePage: number,
    linkContactsPerPage
): LoadLinkContactsSuccessAction => {
    return {
        type: '@@statistics/LINKCONTACTS_SUCCESS',
        payload: {
            linkContactsList,
            totalLinkContactsCount,
            linkContactsActivePage,
            linkContactsPerPage,
            linkContactsLoading: false,
        },
    };
};
export const LoadLinkContactsError: ActionCreator<LoadLinkContactsErrorAction> = (): LoadLinkContactsErrorAction => {
    return {
        type: '@@statistics/LINKCONTACTS_ERROR',
        payload: {
            linkContactsLoading: false,
        },
    };
};

export const ExportLinkContacts: ActionCreator<ExportLinkContactsAction> = (
    params: ExportLinkContactsParams
): ExportLinkContactsAction => ({
    type: '@@statistics/EXPORT_LINKCONTACTS',
    payload: params,
});
export const ExportLinkContactsStart: ActionCreator<ExportLinkContactsStartAction> = (): ExportLinkContactsStartAction => ({
    type: '@@statistics/EXPORT_LINKCONTACTS_START',
});
export const ExportLinkContactsError: ActionCreator<ExportLinkContactsErrorAction> = (): ExportLinkContactsErrorAction => ({
    type: '@@statistics/EXPORT_LINKCONTACTS_ERROR',
});
export const ExportLinkContactsSuccess: ActionCreator<ExportLinkContactsSuccessAction> = (): ExportLinkContactsSuccessAction => ({
    type: '@@statistics/EXPORT_LINKCONTACTS_SUCCESS',
});

export const getStatisticsStore = (store: ApplicationState): StatisticsState => store.statistics;
